import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllmodifiers,
  getSingleModifiersById,
  createModifiers,
  updateModifiers,
  deleteModifiers,
} from "@/api/new-api/staff-management/menu-manage/essential-menu/modifiers"; 

// Thunks

export const fetchAllModifiers = createAsyncThunk(
  "modifiers/fetchAll",
  async ({ restaurant_id, menu_id }, { rejectWithValue }) => {
    const res = await getAllmodifiers(restaurant_id, menu_id);
    if (res.status === "success") return res.data.modifiers;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleModifier = createAsyncThunk(
  "modifiers/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleModifiersById(id);
    if (res.status === "success") return res.data.modifier;
    return rejectWithValue(res.message);
  }
);

export const createNewModifier = createAsyncThunk(
  "modifiers/create",
  async (data, { rejectWithValue }) => {
    const res = await createModifiers(data);
    if (res.status === "success") return res.data.modifier;
    return rejectWithValue(res.message);
  }
);

export const updateExistingModifier = createAsyncThunk(
  "modifiers/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateModifiers(id, data);
    if (res.status === "success") return res.data.modifier;
    return rejectWithValue(res.message);
  }
);

export const deleteModifierById = createAsyncThunk(
  "modifiers/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteModifiers(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State

const initialState = {
  modifiers: [],
  singleModifier: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice

const modifierSlice = createSlice({
  name: "modifiers",
  initialState,
  reducers: {
    clearModifierMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllModifiers.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchAllModifiers.fulfilled, (state, action) => {
        state.getLoading = false;
        state.modifiers = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchAllModifiers.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })

      // Fetch Single
      .addCase(fetchSingleModifier.pending, (state) => {
        state.getLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleModifier.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleModifier = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleModifier.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })

      // Create
      .addCase(createNewModifier.pending, (state) => {
        state.createLoading = true;
        state.error = null;
      })
      .addCase(createNewModifier.fulfilled, (state, action) => {
        state.createLoading = false;
        state.modifiers.push(action.payload);
        state.success = "Modifier created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewModifier.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
      })

      // Update
      .addCase(updateExistingModifier.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateExistingModifier.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.modifiers = state.modifiers.map((mod) =>
          mod.id === action.payload.id ? action.payload : mod
        );
        state.success = "Modifier updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingModifier.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteModifierById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteModifierById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.modifiers = state.modifiers.map((mod) =>
          mod.id === action.payload ? { ...mod, status: "archive" } : mod
        );
        state.success = "Modifier deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteModifierById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearModifierMessages } = modifierSlice.actions;
export default modifierSlice.reducer;
